/**
 * `/agent`: the learner's own AI agent (docs/agents.md), mounted by main.tsx
 * instead of App.
 *
 * Until 2026-10-31 this was the "Use your own agent" section of the paid task
 * board at `/tasks`. The owner retired paid tasks and AI credits that day;
 * the section is what a learner still needs from that page, so it moved here
 * whole (AgentKeys.tsx), and `/tasks#agent` redirects to this address
 * (lib/retired-tasks.ts). Like the board before it, the page boots no virtual
 * machine: a key is made and copied in a few seconds, and the course tab the
 * agent will use is opened separately.
 *
 * The page is AgentView, which takes the account as a prop so a test can
 * render it signed in or out without a Worker.
 */
import { useEffect } from 'react';
import { useAccount } from '../../state/useAccount';
import { useLocale } from '../../state/useLocale';
import { agentPageCopy } from '../../lib/strings/agent-page';
import { COURSE_HREF } from '../../lib/landing';
import { BLOBOR_PATH } from '../../lib/retired-tasks';
import { Toast, useToast } from '../../ui/toast';
import { Brand } from '../../ui';
import { Corner } from '../Corner';
import { AgentKeys } from './AgentKeys';

/** The three things the page reads from the account. */
export interface AgentAccount {
  loaded: boolean;
  signedIn: boolean;
  signIn(): void;
}

export function AgentView({ account }: { account: AgentAccount }) {
  const { locale } = useLocale();
  const s = agentPageCopy[locale];

  useEffect(() => {
    document.title = s.docTitle;
  }, [s]);

  return (
    <main className="projects agent-page">
      <nav className="tasks__nav" aria-label={s.navigation}>
        <Brand href="/" />
        <div>
          <a href={'/' + COURSE_HREF}>{s.navCourse}</a>
          <a href={BLOBOR_PATH}>{s.navBlobor}</a>
        </div>
      </nav>
      <header className="projects__head">
        <p className="projects__kicker">{s.kicker}</p>
        <h1 className="projects__title">{s.title}</h1>
        <p className="projects__lede">{s.lede}</p>
      </header>

      {/* Not before the account is read: a signed-in learner would otherwise
          see the sign-in button flash where their keys are about to be. */}
      {account.loaded ? (
        <AgentKeys signedIn={account.signedIn} onSignIn={account.signIn} />
      ) : (
        <p className="tasks__notice" role="status">{s.loading}</p>
      )}
    </main>
  );
}

export function AgentPage() {
  const { toast, notify, clear } = useToast();
  const account = useAccount(notify);
  return (
    <>
      <Corner />
      <AgentView account={{ loaded: account.loaded, signedIn: account.user !== null, signIn: account.signIn }} />
      <Toast toast={toast} onDone={clear} />
    </>
  );
}

export default AgentPage;
